import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { Button } from './Button'

function IconRefresh({ spinning }: { spinning: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`size-4 ${spinning ? 'animate-spin' : ''}`}
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Manual refresh.
 *
 * Background polling is deliberately slow and pauses when the tab is hidden —
 * it is billed Worker traffic — so there has to be a way to say "check now"
 * after approving something in Seerr rather than waiting out the interval.
 *
 * Invalidates every query rather than one, because the interesting moment is
 * usually "did anything change anywhere", and refreshing the board while
 * leaving the status lamp stale would be its own small lie.
 */
export function RefreshButton({ label = 'Refresh' }: { label?: string }) {
  const queryClient = useQueryClient()
  const fetching = useIsFetching() > 0

  // Held briefly so a cache hit still reads as a deliberate action rather than
  // the button appearing to do nothing.
  const [pressed, setPressed] = useState(false)

  const refresh = useCallback(async () => {
    setPressed(true)
    try {
      await queryClient.invalidateQueries()
    } finally {
      setTimeout(() => setPressed(false), 400)
    }
  }, [queryClient])

  const busy = fetching || pressed

  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={() => void refresh()}
      disabled={busy}
      aria-label={busy ? 'Refreshing' : label}
    >
      <IconRefresh spinning={busy} />
      {/* Labelled at every width: icon-only, this read as a stray glyph rather
          than a control, and nobody found it. */}
      <span>{busy ? 'Refreshing' : label}</span>
    </Button>
  )
}
